import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  Repeat,
  Repeat1,
  Maximize2,
  Minimize2,
  Music,
  ChevronUp,
  ChevronDown,
  Keyboard
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';

export function StreamerPlayerControls({
  currentSong,
  isPlaying,
  currentTime,
  duration,
  volume,
  isLooping,
  isPlaylistLooping,
  isMinimized,
  playlist,
  isPlaylistCollapsed,
  isMuted,
  onPlayPause,
  onSeek,
  onVolumeToggle,
  onVolumeChange,
  onToggleLoop,
  onTogglePlaylistLoop,
  onNext,
  onPrevious,
  onToggleMinimize,
  onTogglePlaylist,
  formatTime,
  showShortcuts,
  setShowShortcuts
}) {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handlePlayPause = async () => {
    console.log('🎯 StreamerPlayerControls: Play button clicked');
    
    if (!currentSong?.file) {
      console.log('❌ StreamerPlayerControls: No file available');
      return;
    }
    
    console.log('🎯 StreamerPlayerControls: Calling onPlayPause...');
    await onPlayPause();
  };

  if (isMinimized) {
    return (
      <div className="h-full flex items-center justify-between px-4">
        {/* Left Side - Playlist Toggle */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('🎯 StreamerPlayerControls: Playlist toggle clicked (minimized), current state:', isPlaylistCollapsed);
              onTogglePlaylist();
            }}
            className="text-white hover:bg-white/10 h-7 w-7 p-0"
            title={isPlaylistCollapsed ? "Show Playlist" : "Hide Playlist"}
          >
            {isPlaylistCollapsed ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
          
          {playlist.length > 0 && (
            <span className="text-xs text-white/60">
              {playlist.length} track{playlist.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Center - Song Info */}
        <div className="flex items-center gap-3 min-w-0 flex-1 justify-center">
          <div className="min-w-0 text-center">
            <p className="font-medium truncate text-white text-xs">
              {currentSong?.title || currentSong?.name || 'No song selected'}
            </p>
            <p className="text-white/60 truncate text-xs">
              {currentSong?.artist || 'Unknown Artist'}
            </p>
          </div>
        </div>

        {/* Right Side - Controls */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={onPrevious}
            className="text-white hover:bg-white/10 h-7 w-7 p-0"
            disabled={!currentSong}
          >
            <SkipBack className="text-white h-3 w-3" />
          </Button>
          
          <button
            onClick={handlePlayPause}
            className="rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all duration-200 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed border-0 w-8 h-8"
            style={{ 
              opacity: !currentSong?.file ? 0.5 : 1,
              cursor: !currentSong?.file ? 'not-allowed' : 'pointer'
            }}
            disabled={!currentSong?.file}
          >
            {isPlaying ? (
              <Pause className="text-white fill-white h-3 w-3" />
            ) : (
              <Play className="ml-0.5 text-white fill-white h-3 w-3" />
            )}
          </button>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={onNext}
            className="text-white hover:bg-white/10 h-7 w-7 p-0"
            disabled={!currentSong}
          >
            <SkipForward className="text-white h-3 w-3" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleMinimize}
            className="text-white hover:bg-white/10 h-7 w-7 p-0"
          >
            <Maximize2 className="text-white h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col justify-center px-4 py-2">
      {/* Progress Bar Section */}
      <div className="mb-2">
        <Slider
          value={[progress]}
          onValueChange={(value) => onSeek((value[0] / 100) * duration)}
          max={100}
          step={0.1}
          className="w-full"
          disabled={!currentSong?.file}
        />
        <div className="flex justify-between text-white/60 mt-1 text-xs">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls Section */}
      <div className="flex items-center justify-between">
        {/* Left Side - Playlist Toggle */}
        <div className="flex items-center gap-2 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              console.log('🎯 StreamerPlayerControls: Playlist toggle clicked (full), current state:', isPlaylistCollapsed);
              onTogglePlaylist();
            }}
            className="text-white hover:bg-white/10 h-8 w-8 p-0"
            title={isPlaylistCollapsed ? "Show Playlist" : "Hide Playlist"}
          >
            {isPlaylistCollapsed ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          
          {playlist.length > 0 && (
            <span className="text-sm text-white/60">
              {playlist.length} track{playlist.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Center - Main Controls */}
        <div className="flex items-center gap-2">
          {/* Shortcuts button to the left of previous */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowShortcuts(true)}
            className="text-white hover:bg-white/10 h-8 w-8 p-0"
            title="Show Keyboard Shortcuts"
          >
            <Keyboard className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrevious}
            className="text-white hover:bg-white/10 h-8 w-8 p-0"
            disabled={!currentSong}
          >
            <SkipBack className="text-white h-4 w-4" />
          </Button>
          
          <button
            onClick={handlePlayPause}
            className="rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all duration-200 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed border-0 w-10 h-10"
            style={{ 
              opacity: !currentSong?.file ? 0.5 : 1,
              cursor: !currentSong?.file ? 'not-allowed' : 'pointer'
            }}
            disabled={!currentSong?.file}
          >
            {isPlaying ? (
              <Pause className="text-white fill-white h-4 w-4" />
            ) : (
              <Play className="ml-0.5 text-white fill-white h-4 w-4" />
            )}
          </button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onNext}
            className="text-white hover:bg-white/10 h-8 w-8 p-0"
            disabled={!currentSong}
          >
            <SkipForward className="text-white h-4 w-4" />
          </Button>
        </div>

        {/* Right Side - Song Info with Album Art Repositioned */}
        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
          {/* Album Art - Moved closer to song info */}
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0 w-10 h-10">
            {currentSong?.artwork ? (
              <img 
                src={currentSong.artwork} 
                alt="Album art" 
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <Music className="text-white h-5 w-5" />
            )}
          </div>

          {/* Song Info - Now directly next to album art */}
          <div className="min-w-0 flex-1 text-left">
            <p className="font-medium truncate text-white text-sm">
              {currentSong?.title || currentSong?.name || 'No song selected'}
            </p>
            <p className="text-white/60 truncate text-xs">
              {currentSong?.artist || 'Unknown Artist'}
            </p>
            {!currentSong?.file && currentSong && (
              <p className="text-xs text-red-400">File not available</p>
            )}
          </div>

          {/* Additional Controls - Moved to far right */}
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleLoop}
              className={`text-white hover:bg-white/10 ${isLooping ? 'text-blue-400' : ''} h-7 w-7 p-0`}
              title="Loop current song"
            >
              <Repeat1 className="text-white h-3 w-3" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onTogglePlaylistLoop}
              className={`text-white hover:bg-white/10 ${isPlaylistLooping ? 'text-blue-400' : ''} h-7 w-7 p-0`}
              title="Loop playlist"
            >
              <Repeat className="text-white h-3 w-3" />
            </Button>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onVolumeToggle}
                className="text-white hover:bg-white/10 h-7 w-7 p-0"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="text-white h-3 w-3" />
                ) : (
                  <Volume2 className="text-white h-3 w-3" />
                )}
              </Button>
              
              <div className="w-16">
                <Slider
                  value={[isMuted ? 0 : volume * 100]}
                  onValueChange={(value) => onVolumeChange([value[0] / 100])}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              <span className="ml-2 text-xs text-white/70 min-w-[32px] text-right select-none">
                {`${Math.round((isMuted ? 0 : volume) * 100)}%`}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleMinimize}
              className="text-white hover:bg-white/10 h-7 w-7 p-0"
            >
              <Minimize2 className="text-white h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}